'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { useTheme } from '@/components/ThemeContext';

import UnifiedCalendarView from '@/components/flow/UnifiedCalendarView';
import GanttChart from '@/components/flow/GanttChart';
import NotificationCenter from '@/components/flow/NotificationCenter';
import {
  IconFolder, IconClipboard, IconCalendar, IconArchive,
  IconBookmark, IconUser, IconUsers, IconSearch,
  IconStar, IconSun, IconMoon, IconMessageCircle,
} from '@/components/flow/FlowIcons';
import {
  ClipboardList, CircleDashed, CheckCircle2, PieChart, FolderOpen, FileText, MessageSquare, AlertTriangle, Folder, Star, Brain, RefreshCw} from 'lucide-react';

const FLOW_PASSWORDS = [
  process.env.NEXT_PUBLIC_FLOW_ACCESS_PASSWORD,
  'Desinsight0219@',
].filter(Boolean);
const AUTH_KEY = 'flow_auth_ok';

// ═══ Password Gate ═══
function PasswordGate({ onAuth }: { onAuth: () => void }) {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (FLOW_PASSWORDS.includes(pw)) {
      sessionStorage.setItem(AUTH_KEY, '1');
      onAuth();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' }}>
      <div className={`w-full max-w-md p-8 rounded-2xl shadow-2xl ${shake ? 'animate-shake' : ''}`}
        style={{ background: 'rgba(30,27,75,0.6)', backdropFilter: 'blur(20px)', border: '1px solid rgba(124,58,237,0.3)' }}>
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center text-white font-bold text-2xl mb-4"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #3b82f6)' }}>S</div>
          <h1 className="text-2xl font-bold text-white">플로우(flow) - 대한민국 최초 AI 협업툴</h1>
          <p className="text-sm mt-2" style={{ color: 'rgba(255,255,255,0.5)' }}>접근하려면 비밀번호를 입력하세요</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <input
              type="password"
              value={pw}
              onChange={e => { setPw(e.target.value); setError(false); }}
              placeholder="비밀번호 입력"
              autoFocus
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all"
              style={{
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                border: error ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.15)',
              }}
            />
            {error && <p className="text-xs mt-2" style={{ color: '#ef4444' }}>비밀번호가 올바르지 않습니다</p>}
          </div>
          <button type="submit"
            className="w-full py-3 rounded-xl text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #7c3aed, #3b82f6)' }}>
            로그인
          </button>
        </form>

        <p className="text-center text-xs mt-6" style={{ color: 'rgba(255,255,255,0.3)' }}>
          © 2026 Desinsight Inc. All rights reserved.
        </p>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-8px); }
          40%, 80% { transform: translateX(8px); }
        }
        .animate-shake { animation: shake 0.4s ease-in-out; }
      `}</style>
    </div>
  );
}

// ═══ Types ═══
interface FlowProject {
  id: string;
  name: string;
  description: string | null;
  color: string;
  folderName: string | null;
  isFavorite: boolean;
  memberCount: number;
  taskCount: number;
  postCount: number;
  fileCount: number;
  updatedAt: string;
}

const PROJECT_COLORS = [
  '#7c3aed', '#3b82f6', '#14b8a6', '#10b981', '#eab308',
  '#f97316', '#ef4444', '#ec4899', '#6b7280', '#8b5cf6',
];

// ═══ Project Create/Edit Modal ═══
function ProjectModal({ project, onSave, onClose }: {
  project?: FlowProject | null;
  onSave: (data: { name: string; description: string; color: string }) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(project?.name || '');
  const [desc, setDesc] = useState(project?.description || '');
  const [color, setColor] = useState(project?.color || '#7c3aed');
  const isEdit = !!project;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave({ name: name.trim(), description: desc.trim(), color });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-md rounded-2xl shadow-2xl p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? '프로젝트 수정' : '새 프로젝트'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>프로젝트명 *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              placeholder="프로젝트 이름 입력" autoFocus
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>설명</label>
            <textarea value={desc} onChange={e => setDesc(e.target.value)}
              placeholder="프로젝트 설명 (선택)" rows={2}
              className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
          </div>
          <div>
            <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--text-muted)' }}>색상</label>
            <div className="flex gap-2">
              {PROJECT_COLORS.map(c => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                  style={{ background: c, border: color === c ? '3px solid var(--text-primary)' : '2px solid transparent' }} />
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm" style={{ color: 'var(--text-muted)' }}>취소</button>
            <button type="submit" disabled={!name.trim()}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: 'var(--accent-blue)' }}>
              {isEdit ? '수정' : '생성'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══ Delete Confirmation ═══
function DeleteConfirm({ projectName, onConfirm, onCancel }: { projectName: string; onConfirm: () => void; onCancel: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div className="w-full max-w-sm rounded-2xl shadow-2xl p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <p className="flex justify-center mb-4"><span className="w-12 h-12 rounded-full flex items-center justify-center bg-red-500/20 text-red-500"><AlertTriangle size={24} /></span></p>
        <h3 className="text-center font-bold mb-2" style={{ color: 'var(--text-primary)' }}>프로젝트 삭제</h3>
        <p className="text-center text-sm mb-4" style={{ color: 'var(--text-muted)' }}>
          <strong>&quot;{projectName}&quot;</strong>를 삭제하시겠습니까?<br/>모든 업무, 게시물이 함께 삭제됩니다.
        </p>
        <div className="flex gap-2">
          <button onClick={onCancel} className="flex-1 px-4 py-2 rounded-lg text-sm" style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}>취소</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white" style={{ background: '#ef4444' }}>삭제</button>
        </div>
      </div>
    </div>
  );
}

type SidebarTab = 'dashboard' | 'projects' | 'tasks' | 'gantt' | 'calendar' | 'files';

// ═══ P3-01: GNB Sidebar ═══
function FlowSidebar({ activeTab, onTabChange, notifCount, onNotifToggle }: {
  activeTab: SidebarTab; onTabChange: (t: SidebarTab) => void;
  notifCount: number; onNotifToggle: () => void;
}) {
  const tabs: { key: SidebarTab; icon: React.ReactNode; label: string }[] = [
    { key: 'dashboard', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="4" rx="1"/><rect x="14" y="10" width="7" height="11" rx="1"/><rect x="3" y="13" width="7" height="8" rx="1"/></svg>, label: '새소식' },
    { key: 'projects', icon: <IconFolder size={18} />, label: '내 프로젝트' },
    { key: 'tasks', icon: <IconClipboard size={18} />, label: '전체 업무' },
    { key: 'gantt', icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="4" y1="6" x2="16" y2="6"/><line x1="8" y1="12" x2="20" y2="12"/><line x1="6" y1="18" x2="14" y2="18"/><circle cx="4" cy="6" r="1.5" fill="currentColor"/><circle cx="8" cy="12" r="1.5" fill="currentColor"/><circle cx="6" cy="18" r="1.5" fill="currentColor"/></svg>, label: '간트차트' },
    { key: 'calendar', icon: <IconCalendar size={18} />, label: '전체 일정' },
    { key: 'files', icon: <IconArchive size={18} />, label: '파일함' },
  ];

  const { theme, toggleTheme } = useTheme();

  return (
    <aside className="flow-sidebar w-56 flex-shrink-0 flex flex-col" style={{ background: 'var(--bg-secondary)', borderRight: '1px solid var(--border-color)' }}>
      {/* Home link */}
      <Link href="/" className="flex items-center gap-2 px-4 py-2 text-[11px] font-medium transition-colors hover:bg-white/5"
        style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-dim)' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>
        desinsight.com
      </Link>
      {/* Logo */}
      <div className="p-4" style={{ borderBottom: '1px solid var(--border-color)' }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ background: 'var(--accent-gradient)' }}>S</div>
          <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>플로우(flow)</span>
          {/* Notification bell */}
          <button onClick={onNotifToggle} className="ml-auto relative w-8 h-8 rounded-lg flex items-center justify-center transition-all hover:bg-white/10"
            style={{ color: 'var(--text-muted)' }} title="알림">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
            {notifCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full text-[10px] font-bold flex items-center justify-center text-white" style={{ background: '#ef4444' }}>
                {notifCount > 9 ? '9+' : notifCount}
              </span>
            )}
          </button>
          {/* Theme toggle */}
          <button onClick={toggleTheme} className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
            style={{ background: 'var(--bg-input)', color: 'var(--text-muted)' }}
            title={theme === 'dark' ? '라이트 모드' : '다크 모드'}>
            {theme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => onTabChange(tab.key)}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all flow-nav-item"
            style={{
              background: activeTab === tab.key ? 'var(--accent-blue)' : 'transparent',
              color: activeTab === tab.key ? '#fff' : 'var(--text-secondary)',
            }}>
            <span className="flex-shrink-0">{tab.icon}</span>
            <span className="font-medium">{tab.label}</span>
          </button>
        ))}

        <div className="pt-4 mt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
          <p className="px-3 text-xs font-medium uppercase tracking-wider mb-2" style={{ color: 'var(--text-dim)' }}>모아보기</p>
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm flow-nav-item"
            style={{ color: 'var(--text-muted)' }}>
            <IconBookmark size={16} /><span>북마크</span>
          </button>
          <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm flow-nav-item"
            style={{ color: 'var(--text-muted)' }}>
            <IconUser size={16} /><span>내 게시물</span>
          </button>
        </div>
      </nav>
    </aside>
  );
}

// ═══ P3-02: Project Card ═══
function ProjectCard({ project, onToggleFavorite, onEdit, onDelete }: {
  project: FlowProject;
  onToggleFavorite: (id: string, newVal: boolean) => void;
  onEdit: (p: FlowProject) => void;
  onDelete: (p: FlowProject) => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const newFav = !project.isFavorite;
    onToggleFavorite(project.id, newFav);
    try {
      await fetch('/api/flow/projects', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: project.id, isFavorite: newFav }),
      });
    } catch {
      onToggleFavorite(project.id, !newFav);
    }
  };

  return (
    <div className="relative group">
      <Link href={`/flow/project/${project.id}`}
        className="block rounded-xl overflow-hidden transition-all flow-card"
        style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <div className="flex">
          <div className="w-1.5 flex-shrink-0" style={{ backgroundColor: project.color }} />
          <div className="flex-1 p-4">
            <div className="flex items-start justify-between">
              <h3 className="font-semibold text-sm mb-1 pr-2 line-clamp-2" style={{ color: 'var(--text-primary)' }}>
                {project.name}
              </h3>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={toggleFavorite} className="transition-transform hover:scale-110">
                  <IconStar size={16} color={project.isFavorite ? '#eab308' : 'var(--text-dim)'} filled={project.isFavorite} />
                </button>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMenuOpen(!menuOpen); }}
                  className="w-6 h-6 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ color: 'var(--text-dim)' }}>⋯</button>
              </div>
            </div>
            {project.description && (
              <p className="text-xs line-clamp-1 mb-3" style={{ color: 'var(--text-muted)' }}>{project.description}</p>
            )}
            <div className="flex items-center gap-3 text-xs" style={{ color: 'var(--text-dim)' }}>
              <span className="flex items-center gap-1"><IconUsers size={13} /> {project.memberCount}</span>
              <span className="flex items-center gap-1"><IconClipboard size={13} /> {project.taskCount}</span>
              <span className="flex items-center gap-1"><IconMessageCircle size={13} /> {project.postCount}</span>
            </div>
          </div>
        </div>
      </Link>
      {/* Context menu */}
      {menuOpen && (
        <div className="absolute right-2 top-10 z-40 w-36 rounded-lg shadow-xl py-1"
          style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <button onClick={() => { setMenuOpen(false); onEdit(project); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-500/10 transition-colors"
 style={{ color: 'var(--text-primary)' }}> 수정</button>
          <button onClick={() => { setMenuOpen(false); onDelete(project); }}
            className="w-full text-left px-3 py-2 text-sm hover:bg-red-500/10 transition-colors"
 style={{ color: '#ef4444' }}> 삭제</button>
        </div>
      )}
      {menuOpen && <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />}
    </div>
  );
}

// ═══ P3-02: Projects Dashboard ═══
function ProjectsDashboard({ projects, onToggleFavorite, onCreateProject, onEditProject, onDeleteProject }: {
  projects: FlowProject[];
  onToggleFavorite: (id: string, newVal: boolean) => void;
  onCreateProject: () => void;
  onEditProject: (p: FlowProject) => void;
  onDeleteProject: (p: FlowProject) => void;
}) {
  const [filter, setFilter] = useState<'all' | 'favorites'>('all');
  const [search, setSearch] = useState('');

  const filtered = projects.filter(p => {
    if (filter === 'favorites' && !p.isFavorite) return false;
    if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const favorites = filtered.filter(p => p.isFavorite);
  const others = filtered.filter(p => !p.isFavorite);

  return (
    <div className="flex-1 overflow-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>내 프로젝트</h1>
        <div className="flex items-center gap-3">
          <button onClick={onCreateProject}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-1.5 transition-all hover:opacity-90"
            style={{ background: 'var(--accent-blue)' }}>
            ＋ 프로젝트 추가
          </button>
          <div className="relative">
            <input type="text" placeholder="프로젝트 검색..." value={search} onChange={e => setSearch(e.target.value)}
              className="px-4 py-2 pl-9 rounded-lg text-sm w-64 outline-none flow-input"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            <IconSearch size={16} color="var(--text-dim)" className="absolute left-3 top-2.5" />
          </div>
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
            <button onClick={() => setFilter('all')}
              className="px-3 py-1.5 text-xs font-medium transition-all"
              style={{ background: filter === 'all' ? 'var(--accent-blue)' : 'transparent', color: filter === 'all' ? '#fff' : 'var(--text-muted)' }}>
              전체
            </button>
            <button onClick={() => setFilter('favorites')}
              className="px-3 py-1.5 text-xs font-medium transition-all"
              style={{ background: filter === 'favorites' ? 'var(--accent-blue)' : 'transparent', color: filter === 'favorites' ? '#fff' : 'var(--text-muted)' }}>
 <Star className="w-4 h-4 inline-block" />즐겨찾기
            </button>
          </div>
        </div>
      </div>

      {/* Favorites */}
      {favorites.length > 0 && (
        <div className="mb-8">
          <h2 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-muted)' }}>
 <Star className="w-4 h-4 inline-block" />즐겨찾기 <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-badge)' }}>{favorites.length}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {favorites.map(p => <ProjectCard key={p.id} project={p} onToggleFavorite={onToggleFavorite} onEdit={onEditProject} onDelete={onDeleteProject} />)}
          </div>
        </div>
      )}

      {/* Others */}
      {others.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-muted)' }}>
            프로젝트 <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-badge)' }}>{others.length}</span>
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {others.map(p => <ProjectCard key={p.id} project={p} onToggleFavorite={onToggleFavorite} onEdit={onEditProject} onDelete={onDeleteProject} />)}
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-20" style={{ color: 'var(--text-dim)' }}>
 <p className="text-4xl mb-3"><Folder className="w-4 h-4 inline-block" /></p>
          <p>프로젝트가 없습니다</p>
        </div>
      )}
    </div>
  );
}

// ═══ Global Tasks View (flow.team 전체 업무 미러링) ═══
interface TaskItem {
  id: string; title: string; status: string; priority: string;
  progress: number; assigneeName: string | null; startDate: string | null;
  dueDate: string | null; sectionName: string | null; flowNumber: number | null;
  project: { id: string; name: string; color: string };
}
interface TaskGroup { project: { id: string; name: string; color: string }; tasks: TaskItem[]; }

const STATUS_CONFIG: Record<string, { label: string; emoji: string; color: string }> = {
 waiting: { label: '요청', emoji: '', color: '#22c55e' },
 in_progress: { label: '진행', emoji: '', color: '#3b82f6' },
 done: { label: '완료', emoji: '', color: '#10b981' },
 hold: { label: '보류', emoji: '', color: '#f59e0b' },
 feedback: { label: '피드백', emoji: '', color: '#8b5cf6' },
};

function AllTasksView() {
  const [groups, setGroups] = useState<TaskGroup[]>([]);
  const [stats, setStats] = useState({ total: 0, waiting: 0, in_progress: 0, done: 0, hold: 0, feedback: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const loadTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter) params.set('status', filter);
      if (search) params.set('q', search);
      const res = await fetch('/api/flow/tasks?' + params.toString());
      const data = await res.json();
      setGroups(data.groups || []);
      setStats(data.stats || { total: 0, waiting: 0, in_progress: 0, done: 0, hold: 0, feedback: 0 });
    } catch { /* fallback */ } finally { setLoading(false); }
  }, [filter, search]);

  useEffect(() => { loadTasks(); }, [loadTasks]);

  const toggleCollapse = (pid: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      next.has(pid) ? next.delete(pid) : next.add(pid);
      return next;
    });
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    try {
      await fetch('/api/flow/tasks/' + taskId + '/status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      loadTasks();
    } catch (e) { console.error('Status change failed:', e); }
  };

  if (loading) return <div className="flex-1 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 overflow-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
          전체 업무
          <span className="text-sm font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-badge)', color: 'var(--text-muted)' }}>
            {stats.total.toLocaleString()}건
          </span>
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input type="text" placeholder="업무 검색..." value={search} onChange={e => setSearch(e.target.value)}
              className="px-3 py-1.5 pl-8 rounded-lg text-sm w-48 outline-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            <IconSearch size={14} color="var(--text-dim)" className="absolute left-2.5 top-2" />
          </div>
        </div>
      </div>

      {/* Status filter chips */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setFilter('')}
          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{ background: !filter ? 'var(--accent-blue)' : 'var(--bg-input)', color: !filter ? '#fff' : 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
          전체 {stats.total}
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button key={key} onClick={() => setFilter(filter === key ? '' : key)}
            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1"
            style={{ background: filter === key ? cfg.color + '22' : 'var(--bg-input)', color: filter === key ? cfg.color : 'var(--text-muted)', border: filter === key ? '1px solid ' + cfg.color + '44' : '1px solid var(--border-color)' }}>
            {cfg.emoji} {cfg.label} {stats[key as keyof typeof stats]}
          </button>
        ))}
      </div>

      {/* Task table */}
      <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
        {/* Table header */}
        <div className="grid grid-cols-12 gap-0 px-4 py-2.5 text-xs font-medium" style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-dim)' }}>
          <div className="col-span-5">업무명</div>
          <div className="col-span-1 text-center">상태</div>
          <div className="col-span-2">담당자</div>
          <div className="col-span-1 text-center">시작일</div>
          <div className="col-span-1 text-center">마감일</div>
          <div className="col-span-1 text-center">진척도</div>
          <div className="col-span-1 text-center">#</div>
        </div>

        {/* Task groups */}
        {groups.map(group => (
          <div key={group.project.id}>
            {/* Group header */}
            <button onClick={() => toggleCollapse(group.project.id)}
              className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-white/5 transition-colors"
              style={{ borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
              <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{collapsed.has(group.project.id) ? '▸' : '▾'}</span>
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: group.project.color }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{group.project.name}</span>
              <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'var(--bg-badge)', color: 'var(--text-dim)' }}>
                {group.tasks.length}
              </span>
              <Link href={'/flow/project/' + group.project.id} className="ml-auto text-xs hover:underline" style={{ color: 'var(--accent-blue)' }}
                onClick={e => e.stopPropagation()}>
                바로가기 →
              </Link>
            </button>

            {/* Tasks */}
            {!collapsed.has(group.project.id) && group.tasks.map(task => {
 const sc = STATUS_CONFIG[task.status] || { label: task.status, emoji: '', color: '#6b7280' };
              const isOverdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'done';
              return (
                <div key={task.id} className="grid grid-cols-12 gap-0 px-4 py-2 items-center hover:bg-white/5 transition-colors text-xs"
                  style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <div className="col-span-5 truncate font-medium" style={{ color: 'var(--text-primary)' }}>{task.title}</div>
                  <div className="col-span-1 text-center">
                    <select value={task.status} onChange={e => handleStatusChange(task.id, e.target.value)}
                      className="text-xs px-1.5 py-0.5 rounded cursor-pointer outline-none"
                      style={{ background: sc.color + '22', color: sc.color, border: 'none' }}>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.emoji} {v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-span-2 truncate" style={{ color: 'var(--text-secondary)' }}>{task.assigneeName || '-'}</div>
                  <div className="col-span-1 text-center" style={{ color: 'var(--text-dim)' }}>{task.startDate ? task.startDate.slice(0, 10) : '-'}</div>
                  <div className="col-span-1 text-center" style={{ color: isOverdue ? '#ef4444' : 'var(--text-dim)' }}>
                    {task.dueDate ? task.dueDate.slice(0, 10) : '-'}
                  </div>
                  <div className="col-span-1 flex items-center gap-1 justify-center">
                    <div className="w-12 h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: task.progress + '%', background: sc.color }} />
                    </div>
                    <span style={{ color: 'var(--text-dim)' }}>{task.progress}%</span>
                  </div>
                  <div className="col-span-1 text-center" style={{ color: 'var(--text-dim)' }}>{task.flowNumber || ''}</div>
                </div>
              );
            })}
          </div>
        ))}

        {groups.length === 0 && (
          <div className="text-center py-16" style={{ color: 'var(--text-dim)' }}>
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
              <ClipboardList size={32} color="var(--text-muted)" />
            </div>
            <p>업무가 없습니다</p>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══ Calendar View → 통합 캘린더 (스케줄러 + 플로우 병합) ═══
function CalendarView() {
  return <UnifiedCalendarView />;
}

// ═══ Gantt Chart View ═══
function GanttView() {
  return (
    <div className="flex-1 overflow-auto p-6">
      <h1 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>간트차트</h1>
      <GanttChart />
    </div>
  );
}

// ═══ Files View (flow.team 파일함) ═══
interface FileItem {
  id: string; fileName: string; fileType: string | null; fileSize: number | null;
  folderPath: string | null; uploaderName: string | null; createdAt: string;
  project: { name: string; color: string };
}

const FILE_ICONS: Record<string, { icon: string; color: string }> = {
 pdf: { icon: '', color: '#ef4444' },
 dwg: { icon: '', color: '#3b82f6' },
 excel: { icon: '', color: '#10b981' },
 image: { icon: '', color: '#f59e0b' },
 document: { icon: '', color: '#6366f1' },
 other: { icon: '', color: '#6b7280' },
};

function formatFileSize(bytes: number | null) {
  if (!bytes) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function FilesView() {
  const [files, setFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/flow/files');
        const data = await res.json();
        setFiles(data.files || []);
      } catch { /* empty */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = files.filter(f => {
    if (search && !f.fileName.toLowerCase().includes(search.toLowerCase())) return false;
    if (typeFilter && f.fileType !== typeFilter) return false;
    return true;
  });

  const fileTypes = [...new Set(files.map(f => f.fileType || 'other'))];

  if (loading) return <div className="flex-1 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-xl font-bold flex items-center gap-3" style={{ color: 'var(--text-primary)' }}>
          파일함
          <span className="text-sm font-normal px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-badge)', color: 'var(--text-muted)' }}>
            {filtered.length}개
          </span>
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input type="text" placeholder="파일 검색..." value={search} onChange={e => setSearch(e.target.value)}
              className="px-3 py-1.5 pl-8 rounded-lg text-sm w-48 outline-none"
              style={{ background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }} />
            <IconSearch size={14} color="var(--text-dim)" className="absolute left-2.5 top-2" />
          </div>
          <div className="flex rounded-lg overflow-hidden" style={{ border: '1px solid var(--border-color)' }}>
            <button onClick={() => setViewMode('grid')}
              className="px-2.5 py-1.5 text-xs transition-all"
              style={{ background: viewMode === 'grid' ? 'var(--accent-blue)' : 'transparent', color: viewMode === 'grid' ? '#fff' : 'var(--text-muted)' }}>▦</button>
            <button onClick={() => setViewMode('list')}
              className="px-2.5 py-1.5 text-xs transition-all"
 style={{ background: viewMode === 'list' ? 'var(--accent-blue)' : 'transparent', color: viewMode === 'list' ? '#fff' : 'var(--text-muted)' }}></button>
          </div>
        </div>
      </div>

      {/* Type filter */}
      <div className="flex gap-2 mb-5 flex-wrap">
        <button onClick={() => setTypeFilter('')}
          className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{ background: !typeFilter ? 'var(--accent-blue)' : 'var(--bg-input)', color: !typeFilter ? '#fff' : 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
          전체
        </button>
        {fileTypes.map(ft => {
          const cfg = FILE_ICONS[ft || 'other'] || FILE_ICONS.other;
          return (
            <button key={ft} onClick={() => setTypeFilter(typeFilter === ft ? '' : (ft || ''))}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1"
              style={{ background: typeFilter === ft ? cfg.color + '22' : 'var(--bg-input)', color: typeFilter === ft ? cfg.color : 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
              {cfg.icon} {ft || 'other'}
            </button>
          );
        })}
      </div>

      {/* File grid/list */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {filtered.map(f => {
            const cfg = FILE_ICONS[f.fileType || 'other'] || FILE_ICONS.other;
            return (
              <div key={f.id} className="rounded-xl p-4 transition-all hover:scale-[1.02] cursor-pointer"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
                <div className="text-3xl mb-3 text-center">{cfg.icon}</div>
                <p className="text-xs font-medium truncate mb-1" style={{ color: 'var(--text-primary)' }}>{f.fileName}</p>
                <p className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{formatFileSize(f.fileSize)}</p>
                <div className="mt-2 flex items-center gap-1">
                  <div className="w-2 h-2 rounded-full" style={{ background: f.project.color }} />
                  <span className="text-[10px] truncate" style={{ color: 'var(--text-muted)' }}>{f.project.name}</span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid var(--border-color)', background: 'var(--bg-card)' }}>
          {filtered.map(f => {
            const cfg = FILE_ICONS[f.fileType || 'other'] || FILE_ICONS.other;
            return (
              <div key={f.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors"
                style={{ borderBottom: '1px solid var(--border-color)' }}>
                <span className="text-lg">{cfg.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>{f.fileName}</p>
                  <p className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{f.project.name} · {f.uploaderName || '-'}</p>
                </div>
                <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{formatFileSize(f.fileSize)}</span>
                <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>
                  {new Date(f.createdAt).toLocaleDateString('ko-KR')}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {filtered.length === 0 && (
        <div className="text-center py-16" style={{ color: 'var(--text-dim)' }}>
          <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)' }}>
            <FolderOpen size={32} color="var(--text-muted)" />
          </div>
          <p>파일이 없습니다</p>
        </div>
      )}
    </div>
  );
}

// ═══ Dashboard View (flow.team 대시보드 미러링) ═══
function DashboardView({ projects }: { projects: FlowProject[] }) {
  const [recentActivity, setRecentActivity] = useState<Array<{ id: string; type: string; title: string; body: string; projectName: string; createdAt: string }>>([]);

  const totalPosts = projects.reduce((sum, p) => sum + p.postCount, 0);
  const totalFiles = projects.reduce((sum, p) => sum + p.fileCount, 0);
  const totalMembers = projects.reduce((sum, p) => sum + p.memberCount, 0);

  useEffect(() => {
    (async () => {
      try {
        const notifRes = await fetch('/api/flow/notifications?limit=10');
        const notifData = await notifRes.json();
        setRecentActivity(notifData.notifications || []);
      } catch { /* empty */ }
    })();
  }, []);

  const statCards = [
    { label: '전체 게시물', value: totalPosts, icon: <FileText size={22} />, color: '#3b82f6', bg: '#3b82f622' },
    { label: '파일', value: totalFiles, icon: <FolderOpen size={22} />, color: '#6366f1', bg: '#6366f122' },
    { label: '프로젝트', value: projects.length, icon: <CheckCircle2 size={22} />, color: '#10b981', bg: '#10b98122' },
    { label: '멤버', value: totalMembers, icon: <PieChart size={22} />, color: '#f59e0b', bg: '#f59e0b22' },
  ];

  return (
    <div className="flex-1 overflow-auto p-6">
      <h1 className="text-xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>새소식</h1>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {statCards.map(card => (
          <div key={card.label} className="rounded-xl p-5 transition-all hover:scale-[1.02]"
            style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-2xl">{card.icon}</span>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: card.bg }}>
                <span className="text-lg font-bold" style={{ color: card.color }}>{typeof card.value === 'number' ? card.value.toLocaleString() : card.value}</span>
              </div>
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{card.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Project status */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--text-primary)' }}>프로젝트 현황</h2>
          <div className="space-y-3">
            {projects.slice(0, 8).map(p => (
              <Link key={p.id} href={'/flow/project/' + p.id} className="flex items-center gap-3 hover:bg-white/5 rounded-lg p-2 transition-colors">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
                <span className="text-sm flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{p.name}</span>
                <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-dim)' }}>
                  <IconMessageCircle size={12} /> {p.postCount}
                </span>
                <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-dim)' }}>
                  <IconUsers size={12} /> {p.memberCount}
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Recent activity */}
        <div className="rounded-xl p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <h2 className="text-sm font-bold mb-4" style={{ color: 'var(--text-primary)' }}>최근 활동</h2>
          <div className="space-y-2">
            {recentActivity.length > 0 ? recentActivity.map(a => (
              <div key={a.id} className="flex gap-3 p-2 rounded-lg hover:bg-white/5 transition-colors">
                <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full" style={{ background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border-color)' }}>
                  {a.type === 'task' ? <ClipboardList size={16} /> : a.type === 'post' ? <FileText size={16} /> : <MessageSquare size={16} />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate" style={{ color: 'var(--text-primary)' }}>{a.title}</p>
                  <p className="text-[10px] truncate" style={{ color: 'var(--text-secondary)' }}>{a.body}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    {a.projectName && <span className="text-[10px] px-1.5 py-0.5 rounded truncate max-w-[150px]" style={{ background: 'var(--bg-badge)', color: 'var(--accent-blue)' }}>{a.projectName}</span>}
                    <span className="text-[10px] whitespace-nowrap" style={{ color: 'var(--text-dim)' }}>
                      {new Date(a.createdAt).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
              </div>
            )) : (
              <p className="text-center py-8 text-sm" style={{ color: 'var(--text-dim)' }}>최근 활동이 없습니다</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══ Knowledge Graph Panel ═══
function KnowledgeGraphPanel() {
  const [graphStats, setGraphStats] = useState<{ projects: number; tasks: number; posts: number; members: number } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncLog, setSyncLog] = useState<string[]>([]);
  const [pgStats, setPgStats] = useState<{ projects: number; tasks: number; posts: number; files: number; members: number; comments: number } | null>(null);

  useEffect(() => {
    // PG 통계
    fetch('/api/flow/projects').then(r => r.json()).then(d => {
      const projects = d.projects?.length || 0;
      setPgStats(prev => ({ ...prev!, projects }));
    }).catch(() => {});
    fetch('/api/flow/tasks').then(r => r.json()).then(d => {
      setPgStats(prev => ({ ...prev!, tasks: d.stats?.total || 0 }));
    }).catch(() => {});
    // Neo4j 통계
    fetch('/api/flow/knowledge-graph').then(r => r.json()).then(d => {
      if (d.graphStats) setGraphStats(d.graphStats);
    }).catch(() => {});
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    setSyncLog(['Flow → Neo4j 지식그래프 동기화 시작...']);
    try {
      const res = await fetch('/api/flow/knowledge-graph', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchSize: 30 }),
      });
      const data = await res.json();
      setSyncLog(data.log || ['완료']);
      if (data.summary) {
        setGraphStats({ projects: data.summary.projects, tasks: data.summary.tasks, posts: data.summary.posts, members: data.summary.people });
      }
    } catch (e: any) {
      setSyncLog(prev => [...prev, `[ERROR] ${e.message}`]);
    } finally {
      setSyncing(false);
    }
  };

  const STATS = [
    { label: 'PostgreSQL 프로젝트', value: pgStats?.projects || 225, icon: '', color: '#7c3aed' },
    { label: 'PostgreSQL 업무', value: pgStats?.tasks || 2301, icon: '', color: '#3b82f6' },
    { label: 'Neo4j 그래프 노드', value: graphStats ? (graphStats.projects + graphStats.tasks + graphStats.posts + graphStats.members) : 0, icon: '', color: '#f59e0b' },
    { label: 'Neo4j 인물 관계', value: graphStats?.members || 0, icon: '', color: '#10b981' },
  ];

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Brain size={22} /> 지식그래프 (Knowledge Graph)
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
            Flow.team의 프로젝트·업무·게시물·파일·담당자 관계를 하나의 Neo4j 지식그래프로 통합 관리합니다
          </p>
        </div>
        <button onClick={handleSync} disabled={syncing}
          className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white flex items-center gap-2 transition-all hover:opacity-90 disabled:opacity-50"
          style={{ background: syncing ? '#6b7280' : 'linear-gradient(135deg, #f59e0b, #ef4444)' }}>
          <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
          {syncing ? '동기화 중...' : 'Neo4j 동기화 실행'}
        </button>
      </div>

      {/* 통계 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {STATS.map(s => (
          <div key={s.label} className="rounded-xl p-4" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
            <div className="text-2xl mb-1">{s.icon}</div>
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value.toLocaleString()}</div>
            <div className="text-xs" style={{ color: 'var(--text-dim)' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* 온톨로지 구조 시각화 */}
      <div className="rounded-xl p-5 mb-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
        <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          온톨로지 관계 구조 (Entity-Relationship)
        </h3>
        <div className="flex items-center justify-center gap-4 flex-wrap text-xs">
          {[
            { from: 'FlowProject', rel: 'HAS_TASK', to: 'FlowTask', color: '#3b82f6' },
            { from: 'FlowProject', rel: 'HAS_POST', to: 'FlowPost', color: '#8b5cf6' },
            { from: 'FlowProject', rel: 'HAS_MEMBER', to: 'FlowMember', color: '#10b981' },
            { from: 'FlowProject', rel: 'HAS_FILE', to: 'FlowFile', color: '#f59e0b' },
            { from: 'Person', rel: 'ASSIGNED_TO', to: 'FlowTask', color: '#ef4444' },
            { from: 'Person', rel: 'AUTHORED', to: 'FlowPost', color: '#ec4899' },
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-1.5 px-3 py-2 rounded-lg" style={{ background: r.color + '11', border: '1px solid ' + r.color + '33' }}>
              <span className="font-bold" style={{ color: r.color }}>{r.from}</span>
              <span style={{ color: 'var(--text-dim)' }}>—[{r.rel}]→</span>
              <span className="font-bold" style={{ color: r.color }}>{r.to}</span>
            </div>
          ))}
        </div>
      </div>

      {/* 동기화 로그 */}
      {syncLog.length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)' }}>
          <div className="px-4 py-2 text-xs font-semibold flex items-center gap-2" style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)' }}>
            <RefreshCw size={14} /> 동기화 로그
          </div>
          <div className="p-4 max-h-80 overflow-auto font-mono text-xs space-y-1" style={{ color: 'var(--text-secondary)' }}>
            {syncLog.map((line, i) => (
              <div key={i} style={{ color: line.startsWith('[OK]') ? '#10b981' : line.startsWith('[ERROR]') ? '#ef4444' : line.startsWith('[DONE]') ? '#f59e0b' : 'var(--text-secondary)' }}>
                {line}
              </div>
            ))}
            {syncing && <div className="animate-pulse" style={{ color: 'var(--accent-blue)' }}>처리 중...</div>}
          </div>
        </div>
      )}
    </div>
  );
}



// ═══ Main Page ═══
export default function FlowPage() {
  const [authed, setAuthed] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>('dashboard');
  const [projects, setProjects] = useState<FlowProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<FlowProject | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<FlowProject | null>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    if (typeof window !== 'undefined' && sessionStorage.getItem(AUTH_KEY) === '1') {
      setAuthed(true);
    }
  }, []);

  // Load notification count
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const res = await fetch('/api/flow/notifications?limit=50');
        const data = await res.json();
        setNotifCount(data.total || 0);
      } catch { /* empty */ }
    })();
  }, [authed]);

  const handleToggleFavorite = useCallback((id: string, newVal: boolean) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, isFavorite: newVal } : p));
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const res = await fetch('/api/flow/projects');
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (e) {
      console.error('Failed to load projects:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadProjects();
  }, [authed, loadProjects]);

  // ── 프로젝트 생성
  const handleCreateProject = async (data: { name: string; description: string; color: string }) => {
    try {
      const res = await fetch('/api/flow/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        setModalMode(null);
        loadProjects();
      }
    } catch (e) { console.error('Create failed:', e); }
  };

  // ── 프로젝트 수정
  const handleEditProject = async (data: { name: string; description: string; color: string }) => {
    if (!editTarget) return;
    try {
      await fetch('/api/flow/projects', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: editTarget.id, ...data }),
      });
      setModalMode(null);
      setEditTarget(null);
      loadProjects();
    } catch (e) { console.error('Edit failed:', e); }
  };

  // ── 프로젝트 삭제
  const handleDeleteProject = async () => {
    if (!deleteTarget) return;
    try {
      await fetch(`/api/flow/projects?id=${deleteTarget.id}`, { method: 'DELETE' });
      setDeleteTarget(null);
      loadProjects();
    } catch (e) { console.error('Delete failed:', e); }
  };

  if (!authed) {
    return <PasswordGate onAuth={() => setAuthed(true)} />;
  }

  return (
    <>
    <div className="h-screen flex pt-14 sm:pt-16" style={{ background: 'var(--bg-primary)' }}>
      <FlowSidebar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        notifCount={notifCount}
        onNotifToggle={() => setNotifOpen(!notifOpen)}
      />

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {activeTab === 'dashboard' && <DashboardView projects={projects} />}
          {activeTab === 'projects' && (
            <ProjectsDashboard
              projects={projects}
              onToggleFavorite={handleToggleFavorite}
              onCreateProject={() => { setEditTarget(null); setModalMode('create'); }}
              onEditProject={(p) => { setEditTarget(p); setModalMode('edit'); }}
              onDeleteProject={(p) => setDeleteTarget(p)}
            />
          )}
          {activeTab === 'tasks' && <AllTasksView />}
          {activeTab === 'gantt' && <GanttView />}
          {activeTab === 'calendar' && <CalendarView />}
          {activeTab === 'files' && <FilesView />}
        </>
      )}

      {/* Notification Panel */}
      <NotificationCenter isOpen={notifOpen} onClose={() => setNotifOpen(false)} />

      {/* Modals */}
      {modalMode === 'create' && (
        <ProjectModal onSave={handleCreateProject} onClose={() => setModalMode(null)} />
      )}
      {modalMode === 'edit' && editTarget && (
        <ProjectModal project={editTarget} onSave={handleEditProject} onClose={() => { setModalMode(null); setEditTarget(null); }} />
      )}
      {deleteTarget && (
        <DeleteConfirm projectName={deleteTarget.name} onConfirm={handleDeleteProject} onCancel={() => setDeleteTarget(null)} />
      )}
    </div>
    </>
  );
}
